<template>
  <transition name="slide" appear>
    <div class="point-panel" ref="panel" id="point-panel">
      <div class="point-panel-container">
        <div class="p-4 m-0 line-height-1 font-weight-bolder bg-light theme-settings-header point-panel-header">
          <span>Map legend</span>
          <div class="point-panel-close" @click="close" @touchend.stop.prevent="close"><i class="ion ion-md-close"></i></div>
        </div>
        <div class="point-panel-content">
          <ul class="list-unstyled">
            <li v-for="point of primaryPointIcons" :key="point.id">
              <img :src="point.src" :alt="`${point.name} marker`" class="marker fpm-marker" />
              <span class="title">{{ point.name }}</span>
            </li>
            <li class="dropdown-divider"></li>
            <li v-for="point of otherPointIcons" :key="point.id">
              <img :src="point.src" :alt="`${point.name} marker`" class="marker fpm-marker" />
              <span class="title">{{ point.name }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { filterDesk, POINT_ICONS, POINTS_MAIN_ORDERED_WITH_BUSY_DESK } from '@/store/modules/point'
import { getIconUrl } from '@/components/leaflet/Icon'

export default {
  name: 'MapLegendPanel',
  props: {
    hasDesk: {
      type: Boolean,
      default: false
    },
    hasArea: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState('pointIcon', ['entities']),
    ...mapGetters('pointIcon', ['pointIcons']),
    availablePointIcons() {
      return this.pointIcons.filter((v) => {
        if (!filterDesk(v.id, this.hasDesk)) return false
        return this.hasArea || v.id !== POINT_ICONS.AREA
      })
    },
    primaryPointIcons() {
      return POINTS_MAIN_ORDERED_WITH_BUSY_DESK.filter((id) => {
        if (!filterDesk(id, this.hasDesk)) return false
        return this.hasArea || id !== POINT_ICONS.AREA
      }).map((id) => {
        const pointIcon = this.entities.pointIcons[id]
        return {
          ...pointIcon,
          src: pointIcon.id === POINT_ICONS.AREA ? '/img/area-icon.png' : getIconUrl(pointIcon.id, pointIcon.file_updated_at)
        }
      })
    },
    otherPointIcons() {
      return this.availablePointIcons
        .filter((v) => !POINTS_MAIN_ORDERED_WITH_BUSY_DESK.includes(v.id))
        .map((v) => ({
          ...v,
          src: getIconUrl(v.id, v.file_updated_at)
        }))
    }
  },
  methods: {
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped lang="scss">
.marker {
  width: 56px;
  background-size: 64px;
  padding: 8px;
}

.title {
  font-weight: bold;
  &:before {
    content: ' - ';
  }
}

.point-panel {
  background: rgba(255, 255, 255, 0.98);
  position: fixed;
  overflow-y: scroll;
  height: 100%;
  right: 0;
  top: 66px;
  padding: 0 0 66px 0;
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.2);
  z-index: 1074;
  width: 100%;

  &-container {
    position: relative;
    min-height: 100%;
  }

  &-header {
    display: flex;
    align-items: center;
  }

  &-close {
    margin-left: auto;
    font-size: 20px;
    cursor: pointer;
  }

  &-content {
    padding: 24px;
  }

  @media print {
    display: none;
  }
  @media (min-width: 600px) {
    flex: 0 0 100%;
    max-width: 100%;
    .desk-destroy {
      right: 80px;
    }
  }

  @media (min-width: 800px) {
    flex: 0 0 60%;
    max-width: 60%;
    .desk-destroy {
      right: calc(60% - 80px);
    }
  }

  @media (min-width: 1000px) {
    flex: 0 0 33%;
    max-width: 33%;
    .desk-destroy {
      right: calc(33% - 80px);
    }
  }

  @media (min-width: 1200px) {
    flex: 0 0 28%;
    max-width: 28%;
    .desk-destroy {
      right: calc(28% - 80px);
    }
  }

  @media (min-width: 1600px) {
    flex: 0 0 25%;
    max-width: 25%;
    .desk-destroy {
      right: calc(25% - 80px);
    }
  }

  @media (min-width: 1900px) {
    flex: 0 0 20%;
    max-width: 20%;
    .desk-destroy {
      right: calc(20% - 80px);
    }
  }

  @media (min-width: 2200px) {
    flex: 0 0 15%;
    max-width: 15%;
    .desk-destroy {
      right: calc(15% - 80px);
    }
  }
}

.slide-enter,
.slide-leave-active {
  transform: translate(100%, 0);
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.5s;
}
</style>
